// ============================================================================
// BESPOKIAN Health - Pharmacist Cockpit Router
// TanStack Router v1.93.1 - File-based routing configuration
// ============================================================================

import { Outlet, createRouter, createRoute, createRootRoute, redirect } from '@tanstack/react-router';
import { Layout } from '@/components/layout/Layout';
import { QueuePage } from '@/pages/Queue';
import { VerificationPage } from '@/pages/Verification';
import { DURReviewPage } from '@/pages/DURReview';
import { FillPage } from '@/pages/Fill';
import { PharmacistCheckPage } from '@/pages/PharmacistCheck';
import { BatchProcessingPage } from '@/pages/BatchProcessing';
import { DashboardPage } from '@/pages/Dashboard';
import { PDMPPage } from '@/pages/PDMP';
import { AuditLogPage } from '@/pages/AuditLog';
import { SettingsPage } from '@/pages/Settings';
import { LoginPage } from '@/pages/Login';
import { LandingPage } from '@/pages/Landing';
import { HowItWorksPage } from '@/pages/HowItWorks';
import { PricingPage } from '@/pages/Pricing';
import { AboutPage } from '@/pages/About';
import { FAQPage } from '@/pages/FAQ';
import { EmployersPage } from '@/pages/Employers';
import { ContactPage } from '@/pages/Contact';
import { ShippingPage } from '@/pages/Shipping';
import { PrivacyPage } from '@/pages/Privacy';
import { TermsPage } from '@/pages/Terms';
import { HIPAAPage } from '@/pages/HIPAA';
import { HIPAATrackingPage } from '@/pages/HIPAATracking';
import { CareersPage } from '@/pages/Careers';
import { RegisterPage } from '@/pages/Register';
import { CartPage } from '@/pages/Cart';
import { CheckoutPage } from '@/pages/Checkout';
import { MedicationsPage } from '@/pages/Medications';

// ============================================================================
// Root Route
// ============================================================================

const rootRoute = createRootRoute({
  component: Outlet,
});

// ============================================================================
// Public Routes
// ============================================================================

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const howItWorksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/how-it-works',
  component: HowItWorksPage,
});

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: PricingPage,
});

const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about',
  component: AboutPage,
});

const faqRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/faq',
  component: FAQPage,
});

const employersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employers',
  component: EmployersPage,
});

const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contact',
  component: ContactPage,
});

const shippingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shipping',
  component: ShippingPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPage,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsPage,
});

const hipaaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hipaa',
  component: HIPAAPage,
});

const hipaaTrackingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hipaa-tracking',
  component: HIPAATrackingPage,
});

const careersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/careers',
  component: CareersPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
});

const cartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cart',
  component: CartPage,
});

const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout',
  component: CheckoutPage,
});

const medicationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/medications',
  component: MedicationsPage,
});

const medicationsCategoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/medications/$category',
  component: MedicationsPage,
});

// ============================================================================
// Authenticated App Shell (Layout)
// ============================================================================

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: Layout,
  beforeLoad: () => {
    // All routes under appRoute require authentication
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw redirect({ to: '/login' });
    }
  },
});

// ============================================================================
// Dashboard
// ============================================================================

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  component: DashboardPage,
});

// ============================================================================
// Queue Routes
// ============================================================================

const queueRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/queue',
  component: QueuePage,
});

const queueVerificationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/queue/verification',
  component: QueuePage,
});

const queueDurRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/queue/dur',
  component: QueuePage,
});

const queueFillRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/queue/fill',
  component: QueuePage,
});

const queueCheckRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/queue/check',
  component: QueuePage,
});

const queueReadyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/queue/ready',
  component: QueuePage,
});

// ============================================================================
// Prescription Detail Routes
// ============================================================================

const rxVerificationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rx/$id/verify',
  component: VerificationPage,
});

const rxDurRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rx/$id/dur',
  component: DURReviewPage,
});

const rxFillRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rx/$id/fill',
  component: FillPage,
});

const rxCheckRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rx/$id/check',
  component: PharmacistCheckPage,
});

const rxPdmpRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rx/$id/pdmp',
  component: PDMPPage,
});

// ============================================================================
// Batch Processing
// ============================================================================

const batchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/batch',
  component: BatchProcessingPage,
});

// ============================================================================
// Audit Log
// ============================================================================

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  component: AuditLogPage,
});

// ============================================================================
// Settings
// ============================================================================

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsPage,
});

// ============================================================================
// Create Router Tree
// ============================================================================

const routeTree = rootRoute.addChildren([
  landingRoute,
  loginRoute,
  registerRoute,
  cartRoute,
  checkoutRoute,
  medicationsRoute,
  medicationsCategoryRoute,
  howItWorksRoute,
  pricingRoute,
  aboutRoute,
  faqRoute,
  employersRoute,
  contactRoute,
  shippingRoute,
  privacyRoute,
  termsRoute,
  hipaaRoute,
  hipaaTrackingRoute,
  careersRoute,
  appRoute.addChildren([
    dashboardRoute,
    queueRoute,
    queueVerificationRoute,
    queueDurRoute,
    queueFillRoute,
    queueCheckRoute,
    queueReadyRoute,
    rxVerificationRoute,
    rxDurRoute,
    rxFillRoute,
    rxCheckRoute,
    rxPdmpRoute,
    batchRoute,
    auditRoute,
    settingsRoute,
  ]),
]);

// ============================================================================
// Export Router
// ============================================================================

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

// Type declarations for router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
